import React, {RefObject, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {Close, Search} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {memo} from '_/utilities/memo';

import {ClearIcon, Input, SearchIcon, StyledSearchBar} from './styles';

/** @deprecated Use ESearchInput from webapp-lib instead. */
export interface Props
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  inputRef?: RefObject<HTMLInputElement>;
  clear?: () => void;
  showSearchIcon?: boolean;
  onChange?: (arg0: string) => void;
  onSearch?: () => void;
}

/** @deprecated Use ESearchInput from webapp-lib instead. */
export function SearchBarComponent({
  inputRef: inputRef_,
  className,
  clear,
  placeholder,
  showSearchIcon = true,
  onChange,
  onSearch,
  ...props
}: Props): React.ReactElement {
  const {t} = useTranslation<I18NextNS>('common');
  const inputInternalRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRef_ || inputInternalRef;
  const changeHandler = useCallback(
    ({target: {value}}: any) => !props.disabled && onChange?.(value),
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      /* istanbul ignore else */
      if (event.key === 'Enter' && inputRef.current?.value) {
        onChange?.(inputRef.current.value);
      }
    },
    [inputRef, onChange]
  );

  const handleFocus = useCallback((): void => {
    inputRef.current?.focus();
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current]);

  const handleClear = useCallback(() => {
    if (props.value) {
      onChange?.('');
      clear?.();
    }
    handleFocus();
    // deprectaed so dependency list has not been checked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clear, props.value, handleFocus]);

  return (
    <div className={className} role='search' {...props}>
      <Input
        ref={inputRef}
        aria-label={t('actions.search')}
        placeholder={placeholder}
        onChange={changeHandler}
        onKeyPress={handleKeyPress}
        {...props}
      />
      {showSearchIcon ? (
        <>
          <ClearIcon
            data-testid='clear-icon'
            tabIndex={0}
            type='button'
            onClick={handleClear}
          >
            <Close />
          </ClearIcon>
          <SearchIcon
            data-testid='search-icon'
            tabIndex={-1}
            type='button'
            onClick={onSearch}
            onFocus={handleFocus}
          >
            <Search />
          </SearchIcon>
        </>
      ) : null}
      <span hidden>{t('actions.search')}</span>
    </div>
  );
}

/** @deprecated Use ESearchInput from webapp-lib instead. */
export default memo(SearchBarComponent, StyledSearchBar);
